import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, List, Modal, Popconfirm, Space, Tabs, message } from 'antd';
import { BookOutlined, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { TextService } from '../../Services/TextService';
import {
    WebApiMemberDtoMemberDto,
    WebApiModelsMemberField,
    WebApiMemberDtoCustomMemberFieldValueDto,
    WebApiModelsMembershipProduct,
    WebApiServicesDataTransferObjectsGroupServiceGroupResultDto,
    WebApiModelsNote,
} from '../../Repository/eventbookyapi';
import { MemberService } from '../../Services/MemberService';
import { EmailNotificationPreference } from '../../Types/EmailNotificationPreference';
import { MemberStatusType } from '../../Types/MemberStatusType';
import { OutPutMemberEditFields } from '../Forms/OutPutMemberFields';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { validateMemberDuplicates } from '../../Helpers/MemberValidationHelper';
import { BookyInput, InputType } from '../Forms/BookyInput';
import { BookyButton } from '../Buttons/BookyButton';
import { formatDateString } from '../../Helpers/DateHelpers';
import FamilyMemberSubList from '../Table/FamilyMemberSubList';
import { useAuth } from '../../Contexts/AuthContext';
import { FamilyMemberConfiguration } from '../../Types/FamilyMemberConfiguration';

interface MemberEditModalProps {
    member: WebApiMemberDtoMemberDto;
    members: WebApiMemberDtoMemberDto[];
    /** This member's household family members (Member rows with primaryMemberId === member.memberId). */
    familyMembers?: WebApiMemberDtoMemberDto[];
    membershipProducts: WebApiModelsMembershipProduct[];
    groups?: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto[];
    memberFieldSettings: WebApiModelsMemberField[];
    user: { hasOnlyReadRights: boolean };
    loadMembers: () => void;
    hasPingPaymentIntegration?: boolean;
    /** Open the modal from the parent instead of the built-in pencil trigger. */
    controlledOpen?: boolean;
    /** Which tab to show when opened via controlledOpen. */
    initialTab?: TabKey;
    /** Hide the built-in pencil/notes trigger icons (when opened purely from the parent). */
    hideTrigger?: boolean;
    /** Notified when the modal opens/closes (for controlledOpen callers). */
    onOpenChange?: (open: boolean) => void;
}

type TabKey = 'details' | 'notes' | 'family';

export const MemberEditModal: React.FC<MemberEditModalProps> = (props) => {
    const GetText = TextService.GetText;
    const { user: authUser } = useAuth();
    const familyMembersEnabled = authUser?.familyMemberConfiguration === FamilyMemberConfiguration.Enabled;
    const isPingPaymentIntegrationActive = !!props.hasPingPaymentIntegration;

    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<TabKey>('details');

    // ---- Details tab state ----
    const [form] = Form.useForm();
    const [groups, setGroups] = useState([]);
    const [warningConfirmed, setWarningConfirmed] = useState(false);

    // ---- Notes tab state ----
    const [notesForm] = Form.useForm();
    const [notes, setNotes] = useState([] as WebApiModelsNote[]);
    const [notesLoaded, setNotesLoaded] = useState(false);
    const [editingNote, setEditingNote] = useState<WebApiModelsNote | null>(null);
    const [editText, setEditText] = useState("");

    // ---- Family members tab ----
    // Family members are Member rows linked via primaryMemberId. Prefer the explicit prop
    // (the member list filters props.members down to primaries); fall back to props.members.
    const familyMembers = (props.familyMembers && props.familyMembers.length > 0)
        ? props.familyMembers
        : (props.members || []).filter(m => m.primaryMemberId === props.member.memberId);
    const familyCount = familyMembers.length;

    const openTab = (tab: TabKey) => {
        setActiveTab(tab);
        setOpen(true);
        if (tab === 'notes') {
            loadNotes();
        }
    };

    // Allow the parent (e.g. a family-member row in the member list) to open this modal
    // directly on a given tab instead of using the built-in pencil trigger.
    useEffect(() => {
        if (props.controlledOpen === true) {
            setActiveTab(props.initialTab ?? 'details');
            setOpen(true);
            if ((props.initialTab ?? 'details') === 'notes') {
                loadNotes();
            }
        } else if (props.controlledOpen === false) {
            setOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.controlledOpen, props.initialTab]);

    const closeModal = () => {
        setOpen(false);
        cancelEdit();
        props.loadMembers();
        props.onOpenChange?.(false);
    };

    // ---- Details tab logic ----
    useEffect(() => {
        if (props.groups) {
            let groups: any = [];

            props.groups.forEach((group: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto) => {
                if (!group.isDefaultGroup) {
                    groups.push({ value: group.id, label: group.name });
                }
            });
            setGroups(groups);
        }

    }, [props])

    useEffect(() => {
        form.setFieldValue("firstName", props.member.firstName);
        form.setFieldValue("lastName", props.member.lastName);
        form.setFieldValue("phoneNumber", props.member.phoneNumber);
        form.setFieldValue("phoneNumber2", props.member.phoneNumber2);
        form.setFieldValue("email", props.member.email);
        form.setFieldValue("email2", props.member.email2);
        form.setFieldValue("address", props.member.address);
        form.setFieldValue("postalCode", props.member.postalCode);
        form.setFieldValue("postalArea", props.member.postalArea);
        form.setFieldValue("adressRow2", props.member.adressRow2);
        form.setFieldValue("adressRow3", props.member.adressRow3);
        form.setFieldValue("personalNumber", props.member.personalNumber);
        form.setFieldValue("webPage", props.member.webPage);
        form.setFieldValue("joinedDate", props.member.joinedDate ? dayjs(props.member.joinedDate) : null);
        form.setFieldValue("company", props.member.company);
        form.setFieldValue("country", props.member.country);
        form.setFieldValue("uniqueId", props.member.uniqueId);
        form.setFieldValue("groups", props.member.groups);
        form.setFieldValue("title", props.member.title);
        form.setFieldValue("extra1", props.member.extra1);
        form.setFieldValue("extra2", props.member.extra2);
        form.setFieldValue("extra3", props.member.extra3);
        form.setFieldValue(
            "status",
            isPingPaymentIntegrationActive
                ? (props.member.status ?? MemberStatusType.Active)
                : (props.member.status == MemberStatusType.Cancelled)
        );
        form.setFieldValue("emailNotificationPreference", props.member.emailNotificationPreference == 1 ? true : false);
        form.setFieldValue("membership", props.member.membershipProduct ? props.member.membershipProduct : null);
        form.setFieldValue("membershipAddon", props.member.membershipProductAddOn ? props.member.membershipProductAddOn : []);

        // Populate custom fields from customFieldsJson
        if (props.member.customFieldValues) {
            try {
                if (Array.isArray(props.member.customFieldValues)) {
                    props.member.customFieldValues.forEach((field: WebApiMemberDtoCustomMemberFieldValueDto) => {
                        if (field && field.fieldId !== undefined && field.fieldValue !== undefined) {
                            // Find the field definition to check uiControl
                            const fieldDef = props.memberFieldSettings?.find((f: any) => f.id === field.fieldId);
                            if (fieldDef && fieldDef.uiControl === 1) { // 1 = DatePicker
                                // Accept YYYY-MM-DD, ISO 8601, or Date object
                                const isValidDate = (val: any) => {
                                    if (!val) return false;
                                    if (typeof val === 'string') {
                                        // Accept YYYY-MM-DD or ISO 8601 (e.g., 2025-08-13T22:00:00.000Z)
                                        return /^\d{4}-\d{2}-\d{2}$/.test(val) || !isNaN(Date.parse(val));
                                    }
                                    if (val instanceof Date && !isNaN(val.getTime())) return true;
                                    return false;
                                };
                                if (isValidDate(field.fieldValue)) {
                                    let dateVal = dayjs(field.fieldValue.toString());
                                    form.setFieldValue(field.fieldId.toString(), dateVal);
                                }
                            } else {
                                form.setFieldValue(field.fieldId.toString(), field.fieldValue);
                            }
                        }
                    });
                }
            } catch (e) {
                // ignore parse errors
            }
        }
    }, [form, props.member])

    const setFieldCustomError = (fieldName: string, error: string,) => {
        form.setFields([
            {
                name: fieldName, // required
                errors: [error],
            },
        ]);
    }

    const onSaveDetails = async (values: any) => {
        // Validate member duplicates
        const validationResult = validateMemberDuplicates(
            values,
            props.members,
            props.memberFieldSettings,
            props.member.memberId,
            props.member
        );

        // Handle errors (blocking)
        if (validationResult.hasErrors) {
            validationResult.errors.forEach(error => {
                setFieldCustomError(error.fieldName, error.message);
            });
            return true;
        }

        // Handle warnings (non-blocking with confirmation)
        if (validationResult.hasWarnings && !warningConfirmed) {
            const warningMessages = validationResult.warnings.map(w => w.message).join('\n\n');
            Modal.confirm({
                title: 'Varning',
                icon: <ExclamationCircleOutlined />,
                content: warningMessages,
                okText: 'Fortsätt ändå',
                cancelText: 'Avbryt',
                onOk: () => {
                    setWarningConfirmed(true);
                    form.submit();
                }
            });
            return true;
        }

        var memberProducts = [];
        if (values.membership) {
            memberProducts.push(values.membership);
        }

        if (values.membershipAddon && Array.isArray(values.membershipAddon)) {
            memberProducts.push(...values.membershipAddon);
        }

        const customFields: WebApiMemberDtoCustomMemberFieldValueDto[] = [];

        props.memberFieldSettings.forEach((field: WebApiModelsMemberField) => {
            if (field.columnType === MemberFieldTypes.Custom) {
                customFields.push({
                    fieldId: field.id!,
                    fieldValue: values[field.id!]
                });
            }
        });

        try {
            await MemberService.UpdateMember({
                memberId: props.member.memberId,
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                email2: values.email2,
                phoneNumber: values.phoneNumber,
                phoneNumber2: values.phoneNumber2,
                address: values.address,
                postalArea: values.postalArea,
                adressRow2: values.adressRow2,
                adressRow3: values.adressRow3,
                personalNumber: values.personalNumber,
                webPage: values.webPage,
                joinedDate: values.joinedDate,
                postalCode: values.postalCode,
                country: values.country,
                company: values.company,
                uniqueId: values.uniqueId,
                title: values.title,
                extra1: values.extra1,
                extra2: values.extra2,
                extra3: values.extra3,
                customFieldValues: customFields,
                memberProducts: memberProducts,
                groups: values.groups,
                status: isPingPaymentIntegrationActive
                    ? (values.status ?? MemberStatusType.Active)
                    : (values.status == true ? MemberStatusType.Cancelled : MemberStatusType.Active),
                emailNotificationPreference: values.emailNotificationPreference == true ? EmailNotificationPreference.NoInformationEmails : EmailNotificationPreference.Unspecified
            });
        } catch (error) {
            message.warning(GetText("EditMember_ErrorSaving"));
            return;
        }
        setOpen(false);
        props.onOpenChange?.(false);

        props.loadMembers();
    }

    const onPhoneChange = (value: any) => {
        form.setFieldValue("phoneNumber", value);
    }

    const onPhoneChange2 = (value: any) => {
        form.setFieldValue("phoneNumber2", value);
    }

    // ---- Notes tab logic ----
    const loadNotes = () => {
        MemberService.GetNotes(props.member.memberId!).then(result => {
            setNotes(result);
            setNotesLoaded(true);
        })
    }

    const notesCount = notesLoaded ? notes.length : (props.member.numberOfNotes || 0);

    const onCreateNote = async () => {
        try {
            await MemberService.AddNote({
                memberId: props.member.memberId,
                text: notesForm.getFieldValue("text"),
            });

            notesForm.setFieldValue("text", "");
            loadNotes();
        } catch (error) {
            message.warning(GetText("EditMember_ErrorSaving"));
            return;
        }
    }

    const deleteNote = (note: WebApiModelsNote) => {
        MemberService.DeleteNote(note.id!).then(() => {
            loadNotes();
        })
    }

    const startEdit = (note: WebApiModelsNote) => {
        setEditingNote(note);
        setEditText(note.text || "");
    }

    const cancelEdit = () => {
        setEditingNote(null);
        setEditText("");
    }

    const saveEdit = async () => {
        if (!editingNote) return;
        try {
            await MemberService.EditNote({
                noteId: editingNote.id,
                text: editText,
            });
            cancelEdit();
            loadNotes();
        } catch (error: any) {
            // The API only lets the author edit a note and answers 400 otherwise.
            if (error?.response?.status === 400) {
                message.warning(GetText("AddNote_OnlyOwnNotes"));
            } else {
                message.warning(GetText("EditMember_ErrorSaving"));
            }
        }
    }

    const notesLocale = {
        emptyText: GetText("AddNote_NoNotes")
    }

    const footer = activeTab === 'details' ? [
        <Button key="cancel" onClick={closeModal}>{GetText("EditMember_ModalCancel")}</Button>,
        <Button
            key="save"
            type="primary"
            disabled={props.user?.hasOnlyReadRights}
            onClick={() => {
                form
                    .validateFields()
                    .then((values) => {
                        onSaveDetails(values);
                    })
                    .catch((info) => {
                        console.log('Validate Failed:', info);
                    });
            }}
        >
            {GetText("EditMember_ModalOk")}
        </Button>,
    ] : [
        <Button key="close" onClick={closeModal}>{GetText("EditMember_ModalClose") || 'Close'}</Button>,
    ];

    return (
        <>
            <Modal
                className={"onefront-modal"}
                open={open}
                title={`(${props.member.uniqueId}) ${props.member.firstName} ${props.member.lastName}`}
                width={800}
                onCancel={closeModal}
                footer={footer}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => openTab(key as TabKey)}
                    items={[
                        {
                            key: 'details',
                            label: GetText("EditMember_ModalTitle"),
                            children: (
                                <>
                                    {props.member.unconfirmedEmail && props.member.unconfirmedEmail.length > 0 ? <i>Medlemmen har inte slutfört registreringen ännu. Registrerade sig med {props.member.unconfirmedEmail}</i> : ""}
                                    <Form disabled={props.user?.hasOnlyReadRights} colon={false} form={form} labelAlign={"left"} layout='vertical' onFinish={onSaveDetails}>
                                        <Col span={24}>
                                            <OutPutMemberEditFields
                                                memberFieldSettings={props.memberFieldSettings}
                                                membershipProducts={props.membershipProducts}
                                                GetText={GetText}
                                                groups={groups}
                                                user={props.user}
                                                onPhoneChange={onPhoneChange}
                                                onPhoneChange2={onPhoneChange2}
                                                isAdmin={true}
                                                hasPingPaymentIntegration={props.hasPingPaymentIntegration}
                                            />
                                        </Col>
                                    </Form>
                                </>
                            ),
                        },
                        {
                            key: 'notes',
                            label: `${GetText("AddNote_ModalTitle")} (${notesCount})`,
                            children: (
                                <>
                                    <Form form={notesForm} labelAlign={"left"} labelCol={{ span: 8, offset: 0 }} wrapperCol={{ span: 16 }} >
                                        <Col span={17}>
                                            <BookyInput type={InputType.multiline} onClick={onCreateNote} name="text" req placeholder={GetText("AddNote_NoteColumnPlaceHolder")} />
                                            <BookyButton
                                                type="primary"
                                                disabled={props.user?.hasOnlyReadRights}
                                                fullWidth={false}
                                                text={GetText("AddNote_SaveNote")}
                                                onClick={onCreateNote}
                                            >
                                            </BookyButton>
                                        </Col>
                                    </Form>

                                    <List
                                        locale={notesLocale}
                                        itemLayout="horizontal"
                                        dataSource={notes}
                                        renderItem={(item) => (
                                            <List.Item extra={
                                                <Space>
                                                    {editingNote?.id !== item.id && (
                                                        <Button disabled={props.user?.hasOnlyReadRights} size="small" icon={<EditOutlined />} onClick={() => startEdit(item)}>{GetText("AddNote_EditNote")}</Button>
                                                    )}
                                                    <Popconfirm
                                                        title={GetText("AddNote_DeleteConfirm")}
                                                        onConfirm={() => deleteNote(item)}
                                                        okText={GetText("Common_Delete")}
                                                        cancelText={GetText("Common_Cancel")}
                                                    >
                                                        <Button disabled={props.user?.hasOnlyReadRights} size="small">{GetText("Common_Delete")}</Button>
                                                    </Popconfirm>
                                                </Space>
                                            }>
                                                <List.Item.Meta
                                                    title={
                                                        <span>
                                                            {item.createdByName} {formatDateString(item.createdAt!.toString())}
                                                            {item.updatedAt && <span style={{ fontStyle: 'italic', color: '#999', marginLeft: 8, fontSize: 12 }}>{GetText("AddNote_Edited")}</span>}
                                                        </span>
                                                    }
                                                    description={
                                                        editingNote?.id === item.id ? (
                                                            <div>
                                                                <textarea
                                                                    style={{ width: '100%', minHeight: 60, marginBottom: 8 }}
                                                                    value={editText}
                                                                    onChange={(e) => setEditText(e.target.value)}
                                                                />
                                                                <Space>
                                                                    <Button type="primary" size="small" onClick={saveEdit}>{GetText("Common_Save")}</Button>
                                                                    <Button size="small" onClick={cancelEdit}>{GetText("AddNote_CancelEdit")}</Button>
                                                                </Space>
                                                            </div>
                                                        ) : item.text
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </>
                            ),
                        },
                        ...(familyMembersEnabled ? [{
                            key: 'family',
                            label: `${GetText('AddFamilyMember_ModalTitle') || 'Familjemedlemmar'} (${familyCount})`,
                            children: (
                                <FamilyMemberSubList
                                    primaryMember={props.member}
                                    familyMembers={familyMembers}
                                    canEdit={!props.user?.hasOnlyReadRights}
                                    groups={props.groups}
                                    loadMembers={props.loadMembers}
                                />
                            ),
                        }] : []),
                    ]}
                />
            </Modal>

            {!props.hideTrigger && <Space size="middle">
                <EditOutlined
                    style={{ fontSize: "20px" }}
                    onClick={() => openTab('details')}
                />

                <Badge color={"blue"} className={props.member.numberOfNotes ? "" : "not-payed"}>
                    <BookOutlined
                        style={{ fontSize: "20px" }}
                        onClick={() => openTab('notes')}
                    />
                </Badge>

                {/* Family tab is reachable by opening the member and clicking the "Familjemedlemmar" tab.
                    Re-enable this shortcut if discoverability of adding the first family member is an issue:
                <Badge color={"blue"} className={familyCount > 0 ? "" : "not-payed"}>
                    <TeamOutlined
                        data-testid="member-family-tab-button"
                        style={{ fontSize: "20px", color: familyCount > 0 ? "#000" : "#bfbfbf" }}
                        onClick={() => openTab('family')}
                    />
                </Badge> */}
            </Space>}
        </>
    );
};

export default MemberEditModal;
